/**
 * Helpers for locating source-mapped blocks in the RENDERED preview DOM.
 *
 * PdfPageViewer renders every section twice: once inside an inert off-screen
 * measurer and once on the visible pages. When a section is taller than a
 * page it is SLICED: every slice page renders a full copy of the section
 * inside a clipped (overflow: hidden) viewport, so one source block can have
 * several non-inert rendered instances, most of them partially or entirely
 * invisible. These helpers therefore expose ALL non-inert instances together
 * with their clip-aware visible geometry, so callers (the selection overlay)
 * can operate on what the user actually sees.
 *
 * The repeatable header and footer multiply instances the same way for a
 * different reason: each is authored once and rendered on every page carrying
 * it, stamps intact, so its id - and the id of every block inside it -
 * resolves to one fully visible instance per such page. Callers that draw
 * per-instance chrome ask for a single ring in that case rather than one down
 * the whole document. Page chrome the renderer invents carries no stamp and
 * never matches; the one exception is the editor's page-break marker, which is
 * deliberately stamped with its `<fp-page-break>` id (the break element itself
 * renders nothing) so the marker stands in as the break's rendered instance.
 *
 * A repeater (fp-lines and friends) stamps its per-item instances with
 * data-fp-instance-of="<owning node id>". Those instances are opaque: a click
 * inside one resolves to the owning repeater's source block, so the whole
 * repeater is selected and its interior is never an editing target. A
 * top-level repeater renders only its stamped instances (no wrapper element
 * carries its data-fp-node), so its instance roots stand in as its rendered
 * presence: geometry and hit-testing fall back to data-fp-instance-of, and
 * renderedBlockNodeId resolves a block's source id from either marker.
 */

import { resolveSelectedNodeId } from "@forgepoint/shared";

/** Attribute a repeater's stamped instances carry, tracing them to the owner. */
export const INSTANCE_OF_ATTRIBUTE = "data-fp-instance-of";

/** Attribute marking hover chrome (drag grip, add button) over the preview. */
export const EDITOR_CHROME_ATTRIBUTE = "data-fp-editor-chrome";

/**
 * Whether a pointer target lies inside the canvas hover chrome. The chrome
 * overlays the preview, so a pointer on it must neither select a block nor move
 * the hover off the block the chrome belongs to.
 * @param target - The event target from the preview
 * @returns True when the target sits inside the hover chrome
 */
export function isEditorChromeTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(`[${EDITOR_CHROME_ATTRIBUTE}]`) !== null
  );
}

/** An axis-aligned rectangle in viewport (client) coordinates. */
export interface ViewportRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

/**
 * Whether a rendered element lives inside the renderer's inert off-screen
 * measurer (a render artifact that must never be an editing target).
 * @param element - The rendered element to test
 * @returns True when the element is inside an inert subtree
 */
export function isRenderArtifact(element: Element): boolean {
  return element.closest("[inert]") !== null;
}

/**
 * Finds every non-inert rendered instance of a source block, in DOM order.
 * A block inside a sliced section has one instance per slice page (plus the
 * measurer copy, which is excluded here); use visiblePartRect to tell which
 * instances actually show on their page. A top-level repeater has no element
 * carrying its own data-fp-node, so its stamped instance roots
 * (data-fp-instance-of) stand in as its rendered instances.
 * @param container - The preview container element
 * @param nodeId - The block's positional node id
 * @returns The block's non-inert rendered instances, empty when none resolve
 */
export function findRenderedBlockInstances(
  container: HTMLElement,
  nodeId: string,
): HTMLElement[] {
  const owned = nonInertElements(
    container.querySelectorAll(`[data-fp-node="${nodeId}"]`),
  );
  if (owned.length > 0) {
    return owned;
  }
  return nonInertElements(
    container.querySelectorAll(`[${INSTANCE_OF_ATTRIBUTE}="${nodeId}"]`),
  );
}

/**
 * Narrows a query result to non-inert HTMLElements, preserving DOM order.
 * @param elements - The queried elements
 * @returns The non-inert HTMLElements among them
 */
function nonInertElements(elements: NodeListOf<Element>): HTMLElement[] {
  return Array.from(elements).filter(
    (element): element is HTMLElement =>
      element instanceof HTMLElement && !isRenderArtifact(element),
  );
}

/**
 * Resolves a pointer-event target to its enclosing rendered source block. A
 * click inside a repeater's stamped instance (any ancestor carrying
 * data-fp-instance-of) resolves to the owning repeater's source block, so a
 * repeater selects as one opaque block and its interior is never targeted.
 * For a top-level repeater the enclosing stamped instance root stands in for
 * the block; read the id back with renderedBlockNodeId.
 * @param target - The event target from the preview
 * @returns The block element carrying data-fp-node or data-fp-instance-of,
 *   or null
 */
export function renderedBlockFromEventTarget(
  target: EventTarget | null,
): HTMLElement | null {
  if (!(target instanceof Element)) {
    return null;
  }
  const nodeId = resolveSelectedNodeId(ancestorMarkers(target));
  if (nodeId === null) {
    return null;
  }
  const enclosing = enclosingRenderedInstance(target, nodeId);
  if (enclosing !== null) {
    return enclosing;
  }
  const body = target.ownerDocument.body;
  return body ? (findRenderedBlockInstances(body, nodeId)[0] ?? null) : null;
}

/**
 * Resolves which rendered copy of a block encloses an element. A block split
 * across pages has one copy per page, so chrome hoisted from a hovered child
 * (a table column's controls hanging off its table) anchors to the copy the
 * pointer is actually in rather than the first one on screen.
 * @param element - An element inside one of the block's rendered copies, or null
 * @param nodeId - The block's positional node id
 * @returns The enclosing non-inert rendered instance, or null when there is none
 */
export function enclosingRenderedInstance(
  element: Element | null,
  nodeId: string,
): HTMLElement | null {
  const enclosing =
    element?.closest(
      `[data-fp-node="${nodeId}"], [${INSTANCE_OF_ATTRIBUTE}="${nodeId}"]`,
    ) ?? null;
  return enclosing instanceof HTMLElement && !isRenderArtifact(enclosing)
    ? enclosing
    : null;
}

/**
 * Resolves the source node id a rendered block traces to: its own
 * data-fp-node, or its owner's id when it is a repeater's stamped instance
 * root.
 * @param block - The block from renderedBlockFromEventTarget, or null
 * @returns The block's source node id, or null
 */
export function renderedBlockNodeId(block: HTMLElement | null): string | null {
  if (block === null) {
    return null;
  }
  return (
    block.getAttribute("data-fp-node") ??
    block.getAttribute(INSTANCE_OF_ATTRIBUTE)
  );
}

/**
 * Collects the marker attributes of a target's ancestor chain, innermost
 * first, feeding the shared pure click resolver.
 * @param target - The clicked element
 * @returns The ancestors' data-fp-node / data-fp-instance-of markers
 */
function ancestorMarkers(
  target: Element,
): { nodeId: string | null; instanceOf: string | null }[] {
  const markers: { nodeId: string | null; instanceOf: string | null }[] = [];
  for (
    let element: Element | null = target;
    element !== null;
    element = element.parentElement
  ) {
    if (isRenderArtifact(element)) {
      return markers;
    }
    markers.push({
      nodeId: element.getAttribute("data-fp-node"),
      instanceOf: element.getAttribute(INSTANCE_OF_ATTRIBUTE),
    });
  }
  return markers;
}

/**
 * Computes the part of a rendered element that survives the renderer's
 * overflow clipping (page boxes and slice viewports): the element's bounding
 * rect intersected with every overflow-hidden/clip ancestor below the
 * scrollable preview container. The container itself never clips, so results
 * stay valid for content scrolled out of view.
 * @param container - The scrollable preview container bounding the walk
 * @param element - The rendered element to measure
 * @returns The visible portion in viewport coordinates, or null when the
 *   element is completely clipped away (an invisible slice copy)
 */
export function visiblePartRect(
  container: HTMLElement,
  element: HTMLElement,
): ViewportRect | null {
  const rect = element.getBoundingClientRect();
  const clipped = clippingAncestors(container, element).reduce(
    (visible: ViewportRect, { ancestor, clipsX, clipsY }): ViewportRect => {
      const ancestorRect = ancestor.getBoundingClientRect();
      return {
        top: clipsY ? Math.max(visible.top, ancestorRect.top) : visible.top,
        left: clipsX ? Math.max(visible.left, ancestorRect.left) : visible.left,
        right: clipsX
          ? Math.min(visible.right, ancestorRect.right)
          : visible.right,
        bottom: clipsY
          ? Math.min(visible.bottom, ancestorRect.bottom)
          : visible.bottom,
      };
    },
    { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom },
  );
  return clipped.right > clipped.left && clipped.bottom > clipped.top
    ? clipped
    : null;
}

/**
 * Computes the combined clip window a rendered element's overflow-clipping
 * ancestors impose (its page box or slice viewport), independent of the
 * element's own rect. Unclipped axes stay at infinity.
 * @param container - The scrollable preview container bounding the walk
 * @param element - The rendered element whose ancestors are inspected
 * @returns The clip window in viewport coordinates, or null when no ancestor
 *   clips
 */
export function clipAncestorBounds(
  container: HTMLElement,
  element: HTMLElement,
): ViewportRect | null {
  const ancestors = clippingAncestors(container, element);
  if (ancestors.length === 0) {
    return null;
  }
  return ancestors.reduce(
    (bounds: ViewportRect, { ancestor, clipsX, clipsY }): ViewportRect => {
      const rect = ancestor.getBoundingClientRect();
      return {
        top: clipsY ? Math.max(bounds.top, rect.top) : bounds.top,
        left: clipsX ? Math.max(bounds.left, rect.left) : bounds.left,
        right: clipsX ? Math.min(bounds.right, rect.right) : bounds.right,
        bottom: clipsY ? Math.min(bounds.bottom, rect.bottom) : bounds.bottom,
      };
    },
    {
      top: Number.NEGATIVE_INFINITY,
      left: Number.NEGATIVE_INFINITY,
      right: Number.POSITIVE_INFINITY,
      bottom: Number.POSITIVE_INFINITY,
    },
  );
}

/** A clipping ancestor and the axes it actually clips on. */
interface ClippingAncestor {
  ancestor: HTMLElement;
  clipsX: boolean;
  clipsY: boolean;
}

/**
 * Lists the elements between a rendered element and the preview container
 * whose overflow clips their content (hidden or clip on either axis).
 * @param container - The preview container bounding the walk (excluded)
 * @param element - The rendered element whose ancestors are inspected
 * @returns The clipping ancestors, innermost first
 */
function clippingAncestors(
  container: HTMLElement,
  element: HTMLElement,
): ClippingAncestor[] {
  const hidesOverflow = (overflow: string) =>
    overflow === "hidden" || overflow === "clip";
  const ancestors: ClippingAncestor[] = [];
  for (
    let ancestor = element.parentElement;
    ancestor !== null && ancestor !== container;
    ancestor = ancestor.parentElement
  ) {
    const style = getComputedStyle(ancestor);
    const clipsX = hidesOverflow(style.overflowX);
    const clipsY = hidesOverflow(style.overflowY);
    if (clipsX || clipsY) {
      ancestors.push({ ancestor, clipsX, clipsY });
    }
  }
  return ancestors;
}
